import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Switch } from '@roger/ui-components';

import { PermissionSlug } from '../../constants/featureFlags';
import { setIntakeTestStatusIntakeIntakeIdTestStatusPatch as setIntakeTestStatus } from '../../services/api/sdk.gen';
import { useStore } from '../../store';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';

interface IntakeTestPatientToggleProps {
  intakeId: string;
  isTest: boolean;
  /** Called after a successful change so the parent can reload the intake from the server. */
  onChanged: () => void | Promise<void>;
  /** Layout/styling classes for the call site (e.g. a bordered strip at the top of the Extraction step). */
  className?: string;
}

/**
 * Permission-gated control to mark an intake (and the patient it becomes) as a
 * test/practice record. ROG-2874.
 *
 * - Rendered ONLY for users with `intake:access` OR `emr:manage` (super-users
 *   inherit it); for everyone else it is absent, not disabled. ROG-3142 widened
 *   the gate to include intake coordinators (`intake:access`) alongside the
 *   existing agency admins (`emr:manage`); the render gate mirrors the backend,
 *   which authorizes either permission.
 * - Non-optimistic: the switch reflects server state only. It does not flip until
 *   the parent reloads the intake after a confirmed save, so a failed save never
 *   leaves a patient *looking* protected when they aren't (false safety).
 */
export function IntakeTestPatientToggle({
  intakeId,
  isTest,
  onChanged,
  className,
}: IntakeTestPatientToggleProps) {
  const { hasAnyPermission, showError } = useStore(
    useShallow(state => ({
      hasAnyPermission: state.hasAnyPermission,
      showError: state.showError,
    }))
  );
  const [saving, setSaving] = useState(false);

  // Absent (not disabled) without permission. Mirrors the backend gate, which
  // authorizes either intake coordinators or agency admins.
  if (
    !hasAnyPermission([PermissionSlug.INTAKE_ACCESS, PermissionSlug.EMR_MANAGE])
  ) {
    return null;
  }

  const handleChange = async (checked: boolean) => {
    setSaving(true);
    try {
      await setIntakeTestStatus({
        path: { intake_id: intakeId },
        body: { is_test: checked },
        throwOnError: true,
      });
      // Pessimistic: reflect server truth only after a confirmed save.
      await onChanged();
    } catch (err) {
      console.error('Failed to update intake test status', err);
      showError(getBusinessErrorMessage(err) ?? 'Failed to update test status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={className}>
      <div className='flex items-center gap-2'>
        <Switch
          id='intake-test-patient-toggle'
          checked={isTest}
          disabled={saving}
          onCheckedChange={handleChange}
          aria-label='Mark as test patient'
        />
        <label
          htmlFor='intake-test-patient-toggle'
          className='cursor-pointer text-sm font-medium'
        >
          Test / practice patient{isTest ? ' · never billed' : ''}
        </label>
      </div>
      <p className='mt-1 text-xs text-muted-foreground'>
        Test patients are never billed: no benefits checks, claims, or NOA, and
        they are excluded from CMS surveys (OASIS &amp; CAHPS).
      </p>
    </div>
  );
}
